import { Link } from 'gatsby';
import { useSelector } from 'react-redux';
import React, { useState } from 'react';
import PropTypes from 'prop-types';

export function ButtonGroup({ children }) {
  return <div className='space-y-7'>{children}</div>;
}

ButtonGroup.propTypes = {
  children: PropTypes.node,
};

export function CustomizableButton(props) {
  const [isHovering, setIsHovering] = useState(false);

  const handleMouseEnter = () => {
    setIsHovering(true);
  };

  const handleMouseLeave = () => {
    setIsHovering(false);
  };

  const sourcing = useSelector((state) => state.sourcing);
  const {
    children,
    elementType,
    isSecondary,
    isCustomizable,
    formSubmit,
    isSubmitting,
    supplementalStyling,
    ...rest
  } = props;
  let customStyles = {};

  if (isCustomizable) {
    const { subscriberData } = sourcing;

    const customBackgroundColor = subscriberData?.button_background_color;
    // if no hover color is provided, keep the background the same on hover
    const customHoverColor =
      subscriberData?.button_background_hover_color || customBackgroundColor;

    // We can't apply custom colors on the fly via Tailwind classes because the Tailwind CSS is compiled at build time.
    // Instead, we will use inline styles. If these are populated, they will override the class-based styling.
    if (isSecondary) {
      customStyles = {
        backgroundColor: isHovering
          ? customHoverColor
          : subscriberData?.button_text_color,
        color: isHovering
          ? subscriberData?.button_text_color
          : customBackgroundColor,
        borderColor: isHovering ? customHoverColor : customBackgroundColor,
      };
    } else {
      customStyles = {
        backgroundColor: isHovering ? customHoverColor : customBackgroundColor,
        color: subscriberData?.button_text_color,
        borderColor: isHovering ? customHoverColor : customBackgroundColor,
      };
    }
  }

  const styleClasses = `btn btn-block btn-lg btn-primary border-2
  hover:bg-primary-focus hover:border-primary-focus focus:bg-primary-focus focus:border-primary-focus 
  disabled:bg-secondary disabled:text-secondary-content ${
    isSecondary ? 'btn-outline' : ''
  } ${isSubmitting ? 'cursor-not-allowed' : ''} ${supplementalStyling || ''}`;

  if (elementType === 'a') {
    return (
      <a
        className={styleClasses}
        style={customStyles}
        {...rest}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
      >
        <span className='leading-tight'>{children}</span>
      </a>
    );
  }

  if (elementType === 'Link') {
    return (
      <Link
        className={styleClasses}
        style={customStyles}
        {...rest}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
      >
        <span className='leading-tight'>{children}</span>
      </Link>
    );
  }

  return (
    // eslint-disable-next-line react/button-has-type
    <button
      className={styleClasses}
      style={customStyles}
      {...rest}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      {isSubmitting && <span className='loading loading-spinner' />}
      {children}
    </button>
  );
}

CustomizableButton.propTypes = {
  children: PropTypes.node,
  elementType: PropTypes.oneOf(['button', 'a', 'Link']),
  isCustomizable: PropTypes.bool,
  isSecondary: PropTypes.bool,
  formSubmit: PropTypes.bool,
  isSubmitting: PropTypes.bool,
  supplementalStyling: PropTypes.string,
};

CustomizableButton.defaultProps = {
  elementType: 'button',
  isCustomizable: true,
  isSecondary: false,
  formSubmit: false,
  isSubmitting: false,
};

export default CustomizableButton;
